import { useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from "lodash";
import dayjs, { Dayjs } from "dayjs";
import { NoUndefinedRangeValueType } from "rc-picker/lib/PickerInput/RangePicker";
import { callsApiService } from "../api/calls";
import { message, Upload } from "antd";
import { usePagination } from "./usePagination";
import { storageService } from "../api/storage";
import { calculateAverageSentimentScore } from "../containers/dashboard-container/dashboard/dashboardFunctions";
import { standardColors } from "../containers/dashboard-container/dashboard/dashboardDatas";
import { useDispatch, useSelector } from "react-redux";
import { RootState, setDateRange, setSearch, setTimezone } from "../store";

// Define TypeScript interfaces for better type safety
interface StatusClassification {
  SUCCESS: number;
  CANDIDATE_ABSENCE_ISSUE: number;
  RATE_LIMITTING_ERROR: number;
}

interface TopicsData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

export const useCallsTable = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set<string>());
  const [callsData, setCallsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sentimentsFilter, setSentimentsFilter] = useState<number[]>();
  const [statusFilter, setStatusFilter] = useState<string[]>();
  const [statusClassificationFilter, setStatusClassificationFilter] =
    useState<string[]>();
  const [topics, setTopics] = useState<string[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [callAnalyticsLoading, setCallAnalyticsLoading] =
    useState<boolean>(false);
  const [topicAnalyticsLoading, setTopicAnalyticsLoading] =
    useState<boolean>(false);
  const [callAnalytics, setCallAnalytics] = useState<any>([]);
  const [avgSentiment, setAvgSentiment] = useState<any>();
  const [topicsData, setTopicsData] = useState<TopicsData>({
    labels: [],
    datasets: [
      {
        label: "Topic Distribution",
        data: [],
        backgroundColor: ["#673AB7"],
      },
    ],
  });
  const [topicCounts, setTopicCounts] = useState<number[]>([]);
  const [statusClassification, setStatusClassification] =
    useState<StatusClassification>({
      SUCCESS: 0,
      CANDIDATE_ABSENCE_ISSUE: 0,
      RATE_LIMITTING_ERROR: 0,
    });
    const [sortField, setSortField] = useState('')
    const [sortOrder, setsortOrder] = useState('')
  const [topicsFilter, setTopicsFilter] = useState<string[]>();

  const { page, pageSize, pageSizeOptions, onPaginationChange } =
    usePagination();

  const dispatch = useDispatch()
  const { dateRange, timezone, rangeType, search } = useSelector((state: RootState) => state.dashboard)

  // Fetch calls data
  const fetchCallsData = useCallback(
    async (params: {
      dateRange?: [string, string];
      timezone?: string;
      search?: string;
      sentiments?: number[];
      status?: string[];
      page?: number;
      statusClassification?: string[];
      sortField?: string,
      sortOrder?: string,
      topics?: string[];
    }) => {
      setLoading(true);
      try {
        const response = await callsApiService.getAllCalls(
          params.dateRange?.[0],
          params.dateRange?.[1],
          params.timezone,
          params.search,
          params.sentiments,
          params.status,
          params.page,
          pageSize,
          params.statusClassification,
          params.sortField,
          params.sortOrder,
          params.topics
        );
        setCallsData(response.data);
        setTotalRecords(response.totalRecords);
      } catch (error) {
        console.error("Error fetching calls data:", error);
        message.error("Failed to fetch calls data");
      } finally {
        setLoading(false);
      }
    },
    [pageSize, page, dateRange, sortField, sortOrder, search, sentimentsFilter, statusClassificationFilter, statusFilter, topicsFilter]
  );

  // Fetch call analytics data
  const getCallAnalyticsData = useCallback(
    async (dateRange?: [string, string], timezone?: string) => {
      setCallAnalyticsLoading(true);
      try {
        const [startDate, endDate] = dateRange || [];
        const response = await callsApiService.getCallAnalytics(
          startDate,
          endDate,
          timezone
        );
        setCallAnalytics(response.data);
        const averageSentiment = calculateAverageSentimentScore(response.data);
        setAvgSentiment(averageSentiment);
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setCallAnalyticsLoading(false);
      }
    },
    [dateRange]
  );

  // Fetch topic analytics data
  const getTopicAnalyticsData = useCallback(
    async (dateRange?: [string, string], timezone?: string) => {
      setTopicAnalyticsLoading(true);
      try {
        const [startDate, endDate] = dateRange || [];
        const response = await callsApiService.getTopicAnalytics(
          startDate,
          endDate,
          timezone
        );
        const topicCounts = response.data;
        const labels: any = Object.keys(topicCounts);
        const dataValues: any = Object.values(topicCounts);
        setTopics(labels);
        setTopicsData({
          labels,
          datasets: [
            {
              label: "Topic Distribution",
              data: dataValues,
              backgroundColor: labels.map((_: any, index:any) => standardColors[index % standardColors.length]),
            },
          ],
        });
        setTopicCounts(dataValues);
      } catch (error) {
        console.error("Error fetching topic analytics:", error);
      } finally {
        setTopicAnalyticsLoading(false);
      }
    },
    [dateRange]
  );

  //sorting
    const handleTableChange = (
        pagination: any,
        filter: any,
      sorter: any,
    ) => {      
      if (sorter.field) {
        const sortOrder = sorter.order === "ascend" ? "asc" : "desc";
        fetchCallsData({
          dateRange,
          timezone,
          search,
          sentiments: sentimentsFilter,
          status: statusFilter,
          page,
          statusClassification: statusClassificationFilter,
          sortField: sorter.field, 
          sortOrder, 
        });
        setSortField(sorter.field)
        setsortOrder(sortOrder)
      }else{
        setSortField('createdAt')
        setsortOrder('desc')
      }
    };

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        fetchCallsData({
          dateRange: dateRange,
          timezone: timezone,
          search: value,
          sentiments: sentimentsFilter,
          status: statusFilter,
          page,
          statusClassification: statusClassificationFilter,
          sortField,
          sortOrder
        });
      }, 1000),
    [
      dateRange,
      timezone,
      sentimentsFilter,
      statusFilter,
      page,
      statusClassificationFilter,
      sortField,
      sortOrder,
      fetchCallsData,
      topicsFilter
    ]
  );

  // Handle search input change
  const handleSearchChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const value = event.target.value;
    dispatch(setSearch(value))
    debouncedSearch(value);
  };

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (dates: NoUndefinedRangeValueType<Dayjs> | null) => {
      if (dates?.[0] && dates?.[1]) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startDate = dates[0].format("YYYY-MM-DD HH:mm:ss");
        const endDate = dates[1].format("YYYY-MM-DD HH:mm:ss");
        dispatch(setDateRange([startDate, endDate]))
        dispatch(setTimezone(timezone))
      }
    },
    []
  );

  // Handle date range type change (if needed)
  const handleDateRangeTypeChange = () => {};

  // Handle file upload
  const beforeUpload = (file: File) => {
    if (file.type !== "text/csv") {
      message.error("Only CSV files are allowed!");
      return Upload.LIST_IGNORE;
    }
    if (selectedFiles.has(file.name)) {
      message.error("This file has already been selected!");
      return Upload.LIST_IGNORE;
    }
    setFile(file);
    setSelectedFiles((prevFiles) => new Set(prevFiles).add(file.name));
    return false;
  };

  const handleUpload = async () => {
    if (!file) {
      message.error("Please select a CSV file to upload.");
      return;
    }
    setLoading(true);
    try {
      const filePath = file.name;
      const uploadUrl = await storageService.getUploadSignedUrl(filePath);
      const response = await fetch(uploadUrl.data.signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": "text/csv",
        },
      });
      if (!response.ok) throw new Error("Failed to upload file");
      message.success("File uploaded successfully!");
      setIsModalVisible(false);
      setFile(null);
      // Refresh data
      fetchCallsData({
        dateRange,
        timezone,
        search,
        sentiments: sentimentsFilter,
        status: statusFilter,
        page,
        statusClassification: statusClassificationFilter,
        sortField,
        sortOrder,
        topics: topicsFilter
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error("Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show/hide modal
  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => {
    setIsModalVisible(false);
    setFile(null);
  };

  // Fetch status classification
  const fetchStatusClassification = useCallback(
    async (dateRange?: [string, string], timezone?: string) => {
      try {
        const [startDate, endDate] = dateRange || [];
        const res = await callsApiService.getStatusClassification(
          startDate,
          endDate,
          timezone
        );
        setStatusClassification(res.data);
      } catch (error) {
        console.error("Error fetching status classification:", error);
      }
    },
    []
  );

  useEffect(() => {
    fetchStatusClassification(dateRange, timezone);
  }, [dateRange, timezone, fetchStatusClassification]);

  // Handle filters
  const handleApplyFilterOnSentiments = (selectedOptions: string[]) => {
    const sentimentMapping: { [key: string]: number[] } = {
      Positive: [1],
      Neutral: [0],
      Negative: [-1],
    };
    const sentimentsToFilter = selectedOptions.flatMap(
      (option) => sentimentMapping[option] || []
    );
    setSentimentsFilter(sentimentsToFilter);
  };

  const handleCancelFilterOnSentiments = () => {
    setSentimentsFilter(undefined);
  };

  const handleApplyFilterOnStatusClassification = (
    selectedOptions: string[]
  ) => {
    setStatusClassificationFilter(selectedOptions);
  };

  const handleCancelFilterOnStatusClassification = () => {
    setStatusClassificationFilter(undefined);
  };

  const handleApplyFilterOnStatus = (selectedOptions: string[]) => {
    setStatusFilter(selectedOptions);
  };

  const handleCancelFilterOnStatus = () => {
    setStatusFilter(undefined);
  };

  const handleApplyFilterOnTopics = (selectedOptions: string[]) => {
    setTopicsFilter(selectedOptions);
  }

  const handleCancelFilterOnTopics = () => {}

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchCallsData({
            dateRange,
            timezone,
            search,
            sentiments: sentimentsFilter,
            status: statusFilter,
            page,
            statusClassification: statusClassificationFilter,
            sortField,
            sortOrder,
            topics: topicsFilter
          }),
        ]);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAllData();
  }, [
    dateRange,
    timezone,
    search,
    sentimentsFilter,
    statusFilter,
    page,
    pageSize,
    statusClassificationFilter,
    sortField,
    sortOrder,
    topicsFilter
  ]);

  useEffect(()=>{
    getCallAnalyticsData(dateRange, timezone),
    getTopicAnalyticsData(dateRange, timezone)
  },[dateRange])

  // Cleanup debounced search on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return {
    isModalVisible,
    setIsModalVisible,
    file,
    setFile,
    selectedFiles,
    setSelectedFiles,
    callsData,
    loading,
    search,
    totalRecords,
    page,
    pageSize,
    pageSizeOptions,
    topics,
    dateRange,
    timezone,
    rangeType,
    sentimentsFilter,
    statusFilter,
    statusClassificationFilter,
    callAnalytics,
    callAnalyticsLoading,
    topicAnalyticsLoading,
    avgSentiment,
    statusClassification,
    topicCounts,
    topicsData,
    topicsFilter,
    handleSearchChange,
    handleDateRangeChange,
    handleDateRangeTypeChange,
    beforeUpload,
    handleUpload,
    showModal,
    handleCancel,
    onPaginationChange,
    fetchCallsData,
    handleApplyFilterOnStatus,
    handleCancelFilterOnStatus,
    handleApplyFilterOnSentiments,
    handleCancelFilterOnSentiments,
    handleApplyFilterOnStatusClassification,
    handleCancelFilterOnStatusClassification,
    handleApplyFilterOnTopics,
    handleCancelFilterOnTopics,
    getCallAnalyticsData,
    getTopicAnalyticsData,
    handleTableChange,
  };
};
